import type { ReactElement } from 'react'

import type { TypeComponentFaqQuestionEntry } from '@contentful-types/component-faq-question'

import { renderRichTextToHtmlString } from '@components/renderer/rich-text'

interface JsonQuestion {
  '@type': 'Question'
  name: string
  acceptedAnswer: {
    '@type': 'Answer'
    text: string
  }
}

interface FaqSchema {
  '@context': 'https://schema.org'
  '@type': 'FAQPage'
  mainEntity: JsonQuestion[]
}

function createFaqSchema(questionsAnswers: { question: string; answer: string }[]): FaqSchema {
  const jsonQuestions: JsonQuestion[] = questionsAnswers.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  }))
  const schema: FaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: jsonQuestions,
  }
  return schema
}

export function FaqSchemaMarkup({ data }: { data: TypeComponentFaqQuestionEntry[] }): ReactElement {
  const questionsAnswers = data.map(({ fields: { question, answer } }) => ({
    question,
    answer: renderRichTextToHtmlString(answer),
  }))

  const structuredData = createFaqSchema(questionsAnswers)

  // This may be more appropriate in the head but there seem to currently be issues with `next/head` and structured data.
  // https://github.com/vercel/next.js/issues/11012
  return (
    <script type="application/ld+json" suppressHydrationWarning>
      {/**
       * Client side only because React seems to escape additional characters when server-side rendering.
       * This causes a hydration error when the server and client side output do not match.
       * An alternate way to fix this is to use `dangerouslySetInnerHTML` to prevent escaping.
       */}
      {IS_CLIENT_SIDE ? JSON.stringify(structuredData) : null}
    </script>
  )
}
